<template>
  <el-upload
    ref="uploadRef"
    class="table-upload"
    name="file"
    :action="action"
    :headers="{ Authorization: getBearerToken }"
    multiple
    :accept="accept"
    :on-preview="handlePreview"
    :on-error="handleError"
    :on-success="handleSuccess"
    :on-remove="handleRemove"
    :before-upload="handleBeforeUpload"
  >
    <template v-if="!isReadonly">
      <el-button slot="trigger" type="text" icon="el-icon-upload" class="feed">
        {{ buttonText }}
      </el-button>
    </template>

    <template slot="file" slot-scope="{ file }">
      <div class="el-upload-list__item-content" :class="{ 'has-error': file.isError }">
        <a class="el-upload-list__item-name" @click="handlePreview(file)">
          <i class="el-icon-document"></i>
          <span v-if="file.isError" class="hint-tag">[{{ file.remark }}]</span>
          <span class="file-name-text">{{ file.name }}</span>
        </a>
        <template v-if="!isReadonly">
          <label class="el-upload-list__item-status-label">
            <i class="el-icon-upload-success el-icon-circle-check"></i>
          </label>
          <i class="el-icon-close" @click="handleRemove(file)"></i>
        </template>
      </div>
    </template>
  </el-upload>
</template>

<script>
import { mapGetters } from "vuex";
import { generateUUID } from "@/utils/common";

export default {
  name: "TableUpload",
  props: {
    value: { type: Array, default: () => [] },
    action: { type: String, required: true },
    buttonText: { type: String, default: "" },
    accept: {
      type: String,
      default: "image/jpg,image/jpeg,image/png,application/pdf,application/msword",
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      _uploadingCount: 0,
      _uploadedCache: [],
    };
  },
  computed: {
    ...mapGetters("user", ["getBearerToken"]),
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this._uploadedCache = [...(val || [])];
        this.$nextTick(() => {
          this.syncServerFiles();
        });
      },
    },
  },
  methods: {
    syncServerFiles() {
      const upload = this.$refs.uploadRef;
      if (!upload) return;
      if (this._uploadingCount > 0) return;

      if (!this.value || this.value.length === 0) {
        upload.uploadFiles.splice(0);
        return;
      }

      const getFileUrl = f => f.rawOrigin?.url || f.response?.data?.urls?.[0]?.url || f.url || "";

      const valueUrlMap = new Map();
      this.value.forEach(item => {
        const key = item.url || "";
        if (key) valueUrlMap.set(key, item);
      });

      for (let i = upload.uploadFiles.length - 1; i >= 0; i--) {
        const f = upload.uploadFiles[i];
        if (f.status !== "success") continue;
        const key = getFileUrl(f);
        if (key && !valueUrlMap.has(key)) {
          upload.uploadFiles.splice(i, 1);
        }
      }

      this.value.forEach(item => {
        const key = item.url || "";
        if (!key) return;
        const exists = upload.uploadFiles.some(f => getFileUrl(f) === key);
        if (exists) return;
        upload.uploadFiles.push({
          uid: generateUUID(),
          fid: item.fid || "",
          name: item.name || "",
          size: item.size || 0,
          url: item.url || "",
          status: "success",
          isError: item.status === 1,
          remark: item.remark || "",
          rawOrigin: item,
        });
      });
    },
    handleBeforeUpload(file) {
      this._uploadingCount++;
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this._uploadingCount--;
        this.$message.warning(this.$t("common.warning.uploadSize"));
      }
      return isLt5M;
    },
    handlePreview(file) {
      const url = file.url || file.response?.data?.urls?.[0]?.url;
      if (url) {
        window.open(url);
      }
    },
    handleError(err, file) {
      this.$alertError(err);
      this._uploadingCount--;
      const upload = this.$refs.uploadRef;
      if (upload) {
        const idx = upload.uploadFiles.indexOf(file);
        if (idx !== -1) upload.uploadFiles.splice(idx, 1);
      }
    },
    handleSuccess(res, file) {
      this._uploadingCount--;

      if (res.code !== 200) {
        this.$alertError(res);
        const upload = this.$refs.uploadRef;
        if (upload) {
          const idx = upload.uploadFiles.indexOf(file);
          if (idx !== -1) upload.uploadFiles.splice(idx, 1);
        }
        return;
      }

      if (!res.data?.urls?.[0]) return;

      const upload = this.$refs.uploadRef;
      if (upload) {
        this._uploadedCache = this.buildResultFromFiles(upload.uploadFiles);
        this.$emit("input", [...this._uploadedCache]);
      }
    },
    handleRemove(file) {
      const upload = this.$refs.uploadRef;
      if (!upload) return;

      const idx = upload.uploadFiles.indexOf(file);
      if (idx !== -1) upload.uploadFiles.splice(idx, 1);

      this._uploadedCache = this.buildResultFromFiles(upload.uploadFiles);
      this.$emit("input", [...this._uploadedCache]);
    },
    buildResultFromFiles(files) {
      return files
        .filter(f => f.status === "success")
        .map(f => {
          if (f.rawOrigin) return f.rawOrigin;
          const d = f.response?.data?.urls?.[0];
          return {
            fid: d?.fid || f.fid,
            name: d?.name || f.name,
            size: d?.size || f.size || 0,
            url: d?.url || "",
          };
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.table-upload {
  line-height: 0;

  ::v-deep {
    .el-upload--text {
      display: inline-block;
      background-color: transparent;
      border: none;
      border-radius: 0;
      width: inherit;
      height: inherit;
    }

    .el-upload--text .el-icon-upload {
      margin: 0;
      line-height: 1em;
      font-size: 14px;
      color: #0060ff;
    }

    .el-upload-list {
      text-align: left;
    }

    .hint-tag {
      color: #f56c6c;
      font-weight: bold;
      margin-right: 4px;
      font-size: 13px;
    }

    .has-error .file-name-text {
      color: #f56c6c;
    }
  }
}
</style>
